import { useEffect, useMemo, useState } from "react";
import { ChevronDown,ChevronUp,Loader2,MessageSquareText } from "lucide-react";
import { getAnonymousFeedbacks, updateAnonymousFeedbackStatus } from "../../services/wisdomServices/growthService";
import styles from "./styles.module.css";

const MAX_TITLE_LENGTH = 100;
const MAX_MESSAGE_LENGTH = 255;

const formatDate = (date) => {
    if (!date) return "-";

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
        return "-";
    }

    return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
    }).format(parsedDate);
};

const formatValue = (value = "") => {
    return value
        .replaceAll("_", " ")
        .replaceAll("-", " ")
        .split(" ")
        .filter(Boolean)
        .map(
            (word) =>
                word.charAt(0).toUpperCase() +
                word.slice(1)
        )
        .join(" ");
};

function AnonymousFeedback() {
    const [feedbacks, setFeedbacks] = useState([]);
    const [expandedItems, setExpandedItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [updatingStatusId, setUpdatingStatusId] = useState(null);

    useEffect(() => {
        loadFeedbacks();
    }, []);

    const loadFeedbacks = async () => {
        try {
            setLoading(true);

            const response =
                await getAnonymousFeedbacks();

            const items = Array.isArray(response)
                ? response
                : response?.data ?? [];

            setFeedbacks(items);
        } catch (error) {
            console.error(
                "Error loading anonymous feedbacks:",
                error
            );

            console.error(
                "API response:",
                error?.response?.data
            );

            setFeedbacks([]);
        } finally {
            setLoading(false);
        }
    };

    const toggleItem = (id) => {
        setExpandedItems((previous) =>
            previous.includes(id)
                ? previous.filter(
                    (itemId) => itemId !== id
                )
                : [...previous, id]
        );
    };

    const updateField = (id, field, value) => {
        setEditForms((previous) => ({
            ...previous,
            [id]: {
                ...previous[id],
                [field]: value
            }
        }));
    };

    const handleStatusChange = async (
        item,
        status
    ) => {
        if (status === item.status) return;

        try {
            setUpdatingStatusId(item.id);

            await updateAnonymousFeedbackStatus(
                item.id,
                status
            );

            setFeedbacks((previous) =>
                previous.map((feedback) =>
                    feedback.id === item.id
                        ? {
                            ...feedback,
                            status
                        }
                        : feedback
                )
            );
        } catch (error) {
            console.error(
                "Error updating feedback status:",
                error
            );

            console.error(
                "API response:",
                error?.response?.data
            );
        } finally {
            setUpdatingStatusId(null);
        }
    };

    const newCount = useMemo(
        () =>
            feedbacks.filter(
                (item) => item.status === "new"
            ).length,
        [feedbacks]
    );

    const inProgressCount = useMemo(
        () =>
            feedbacks.filter(
                (item) =>
                    item.status === "in-progress"
            ).length,
        [feedbacks]
    );

    const closedCount = useMemo(
        () =>
            feedbacks.filter(
                (item) => item.status === "closed"
            ).length,
        [feedbacks]
    );

    const getSeverityClass = (severity) => {
        switch (severity) {
            case "low":
                return styles.lowSeverity;

            case "medium":
                return styles.mediumSeverity;

            case "high":
                return styles.highSeverity;

            case "urgent":
                return styles.urgentSeverity;

            default:
                return "";
        }
    };

    const getSentimentClass = (sentiment) => {
        switch (sentiment) {
            case "positive":
                return styles.positiveSentiment;

            case "constructive":
                return styles.constructiveSentiment;

            case "negative":
                return styles.negativeSentiment;

            default:
                return "";
        }
    };

    return (
        <div className={styles.page}>
            <div className={styles.pageHeader}>
                <div>
                    <div className={styles.titleRow}>
                        <h1>Anonymous Feedback</h1>

                        <span
                            className={
                                styles.totalBadge
                            }
                        >
                            {feedbacks.length} items
                        </span>
                    </div>

                    <p
                        className={
                            styles.pageDescription
                        }
                    >
                        Review and manage anonymous
                        feedback submitted by team
                        members.
                    </p>
                </div>
            </div>

            <div className={styles.summary}>
                <div className={styles.summaryItem}>
                    <span
                        className={
                            styles.summaryValue
                        }
                    >
                        {newCount}
                    </span>

                    <span
                        className={
                            styles.summaryLabel
                        }
                    >
                        New
                    </span>
                </div>

                <div
                    className={
                        styles.summaryDivider
                    }
                />

                <div className={styles.summaryItem}>
                    <span
                        className={
                            styles.summaryValue
                        }
                    >
                        {inProgressCount}
                    </span>

                    <span
                        className={
                            styles.summaryLabel
                        }
                    >
                        In Progress
                    </span>
                </div>

                <div
                    className={
                        styles.summaryDivider
                    }
                />

                <div className={styles.summaryItem}>
                    <span
                        className={
                            styles.summaryValue
                        }
                    >
                        {closedCount}
                    </span>

                    <span
                        className={
                            styles.summaryLabel
                        }
                    >
                        Closed
                    </span>
                </div>
            </div>

            <div className={styles.feedbackList}>
                {loading ? (
                    <div
                        className={
                            styles.emptyState
                        }
                    >
                        <Loader2
                            size={20}
                            className={
                                styles.spinner
                            }
                        />

                        Loading feedbacks...
                    </div>
                ) : feedbacks.length === 0 ? (
                    <div
                        className={
                            styles.emptyState
                        }
                    >
                        No anonymous feedbacks found.
                    </div>
                ) : (
                    feedbacks.map((item) => {
                        const isExpanded = expandedItems.includes(item.id);
                        const isUpdatingStatus = updatingStatusId === item.id;

                        return (
                            <article
                                key={item.id}
                                className={styles.feedbackCard}
                            >
                                <div
                                    className={
                                        styles.cardHeader
                                    }
                                    role="button"
                                    tabIndex={0}
                                    onClick={() =>
                                        toggleItem(
                                            item.id
                                        )
                                    }
                                    onKeyDown={(
                                        event
                                    ) => {
                                        if (
                                            event.key ===
                                                "Enter" ||
                                            event.key ===
                                                " "
                                        ) {
                                            toggleItem(
                                                item.id
                                            );
                                        }
                                    }}
                                >
                                    <div
                                        className={
                                            styles.cardHeaderContent
                                        }
                                    >
                                        <div
                                            className={
                                                styles.cardTitleRow
                                            }
                                        >
                                            <span
                                                className={
                                                    styles.feedbackIcon
                                                }
                                            >
                                                <MessageSquareText
                                                    size={
                                                        17
                                                    }
                                                />
                                            </span>

                                            <h2>
                                                {formatValue(
                                                    item.category
                                                )}
                                            </h2>

                                            <span
                                                className={`${styles.severityBadge} ${getSeverityClass(item.severity)}`}
                                            >
                                                {formatValue(
                                                    item.severity
                                                )}
                                            </span>

                                            <span
                                                className={`${styles.sentimentBadge} ${getSentimentClass(item.sentiment)}`}
                                            >
                                                {formatValue(
                                                    item.sentiment
                                                )}
                                            </span>
                                        </div>

                                        <p
                                            className={
                                                styles.messagePreview
                                            }
                                        >
                                            {
                                                item.message
                                            }
                                        </p>

                                        <div
                                            className={
                                                styles.metaRow
                                            }
                                        >
                                            {item.team && (
                                                <span>
                                                    <i className="pi pi-users" />
                                                    {
                                                        item.team
                                                    }
                                                </span>
                                            )}

                                            <span>
                                                <i className="pi pi-calendar" />
                                                {formatDate(
                                                    item.createdAt
                                                )}
                                            </span>

                                            {item.updatedByName && (
                                                <span>
                                                    <i className="pi pi-user-edit" />
                                                    Updated
                                                    by{" "}
                                                    {
                                                        item.updatedByName
                                                    }
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    <div
                                        className={
                                            styles.headerActions
                                        }
                                    >
                                        <select
                                            value={
                                                item.status ||
                                                "new"
                                            }
                                            className={
                                                styles.statusSelect
                                            }
                                            disabled={
                                                isUpdatingStatus
                                            }
                                            onClick={(
                                                event
                                            ) =>
                                                event.stopPropagation()
                                            }
                                            onChange={(
                                                event
                                            ) => {
                                                event.stopPropagation();

                                                handleStatusChange(
                                                    item,
                                                    event
                                                        .target
                                                        .value
                                                );
                                            }}
                                        >
                                            <option value="new">
                                                New
                                            </option>

                                            <option value="in-progress">
                                                In Progress
                                            </option>

                                            <option value="closed">
                                                Closed
                                            </option>
                                        </select>

                                        {isUpdatingStatus && (
                                            <Loader2
                                                size={
                                                    16
                                                }
                                                className={
                                                    styles.spinner
                                                }
                                            />
                                        )}

                                        <button
                                            type="button"
                                            className={
                                                styles.expandButton
                                            }
                                            onClick={(
                                                event
                                            ) => {
                                                event.stopPropagation();

                                                toggleItem(
                                                    item.id
                                                );
                                            }}
                                        >
                                            {isExpanded ? (
                                                <ChevronUp
                                                    size={
                                                        20
                                                    }
                                                />
                                            ) : (
                                                <ChevronDown
                                                    size={
                                                        20
                                                    }
                                                />
                                            )}
                                        </button>
                                    </div>
                                </div>

                                
                                {isExpanded && (
                                    <div className={styles.cardContent}>
                                        <div className={styles.formGrid}>
                                            <div className={styles.formGroup}>
                                                <label>Category</label>
                                                <input
                                                    type="text"
                                                    value={formatValue(item.category)}
                                                    readOnly
                                                />
                                            </div>

                                            <div className={styles.formGroup}>
                                                <label>Severity</label>
                                                <input
                                                    type="text"
                                                    value={formatValue(item.severity)}
                                                    readOnly
                                                />
                                            </div>
                                        </div>

                                        <div className={styles.formGrid}>
                                            <div className={styles.formGroup}>
                                                <label>Sentiment</label>
                                                <input
                                                    type="text"
                                                    value={formatValue(item.sentiment)}
                                                    readOnly
                                                />
                                            </div>

                                            <div className={styles.formGroup}>
                                                <label>Team</label>
                                                <input
                                                    type="text"
                                                    value={item.team || ""}
                                                    readOnly
                                                />
                                            </div>
                                        </div>

                                        <div className={styles.formGroup}>
                                            <label>Message</label>
                                            <textarea
                                                rows={5}
                                                value={item.message || ""}
                                                readOnly
                                            />
                                        </div>

                                        <div className={styles.detailsFooter}>
                                            <div className={styles.updateInfo}>
                                                <span>
                                                    Created: {formatDate(item.createdAt)}
                                                </span>

                                                {item.updatedAt && (
                                                    <span>
                                                        Last update: {formatDate(item.updatedAt)}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                            </article>
                        );
                    })
                )}
            </div>
        </div>
    );
}

export default AnonymousFeedback;